import { toast } from 'react-toastify';

const BACKEND = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/?$/, '/');

export const MCP_CATALOG_API_BASE = `${BACKEND}synctrack/mcp-catalog/admin/endpoints`;

export const getMcpCatalogAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
    };
};

export const buildMcpCatalogListUrl = ({ globalToolId, mcId, connectorKey, operationKey } = {}) => {
    const params = new URLSearchParams({ active: 'false' });
    if (globalToolId != null && globalToolId !== '') {
        params.set('global_tool_id', String(globalToolId));
    }
    if (mcId) params.set('mc_id', String(mcId));
    if (connectorKey) params.set('connector_key', connectorKey);
    if (operationKey) params.set('operation_key', operationKey);
    return `${BACKEND}synctrack/mcp-catalog/global/endpoints?${params.toString()}`;
};

/** Normalize master connector to catalog mec_connector_key (prefer mc_connector_type). */
export const deriveConnectorKeyFromMasterConnector = (connector) => {
    if (!connector) return '';
    const raw =
        connector.mc_connector_type ||
        connector.mcConnectorType ||
        connector.mc_name ||
        connector.mcName ||
        '';
    return String(raw)
        .trim()
        .toLowerCase()
        .replace(/[\s-]+/g, '_')
        .replace(/[^a-z0-9_]/g, '');
};

export const resolveConnectorKeyForTool = (tool, masterConnectors = []) => {
    if (!tool) return '';
    const mcId = tool.mcId || tool.mc_id;
    if (!mcId) return '';
    const connector = masterConnectors.find(
        (c) => c.mc_id === mcId || c.mcId === mcId
    );
    return deriveConnectorKeyFromMasterConnector(connector);
};

export const isCdxUser = () => {
    try {
        return localStorage.getItem('cdx_user') === 'true';
    } catch {
        return false;
    }
};

export const stringifyJsonField = (val) => {
    if (val == null || val === '') return '';
    if (typeof val === 'object') return JSON.stringify(val, null, 2);
    return String(val);
};

const safeStr = (val) => (val == null ? '' : String(val));

const parseJsonOrThrow = (label, text) => {
    const t = (text || '').trim();
    if (!t) return {};
    try {
        return JSON.parse(t);
    } catch (e) {
        throw new Error(`${label}: invalid JSON (${e.message})`);
    }
};

export const mapFormToBody = (data) => {
    const trim = (v) => safeStr(v).trim();
    const body = {
        mec_endpoint_key: trim(data.mec_endpoint_key),
        mec_connector_key: trim(data.mec_connector_key),
        mec_operation_key: trim(data.mec_operation_key),
        version: Number(data.version) || 1,
        active: !!data.active,
        mec_application: trim(data.mec_application),
        mec_method: trim(data.mec_method).toUpperCase(),
        mec_path: trim(data.mec_path),
        mec_auth_type: trim(data.mec_auth_type || 'bearer').toLowerCase(),
        mec_auth_config: parseJsonOrThrow('mec_auth_config', data.mec_auth_config),
        mec_is_secondary_token: !!data.mec_is_secondary_token,
        mec_safety_level: trim(data.mec_safety_level || 'read').toLowerCase(),
        mec_default_base_url: trim(data.mec_default_base_url) || null,
        mec_default_headers: parseJsonOrThrow('mec_default_headers', data.mec_default_headers),
        mec_default_query_params: parseJsonOrThrow('mec_default_query_params', data.mec_default_query_params),
        mec_path_params_schema: trim(data.mec_path_params_schema)
            ? parseJsonOrThrow('mec_path_params_schema', data.mec_path_params_schema)
            : {},
        mec_query_params_schema: trim(data.mec_query_params_schema)
            ? parseJsonOrThrow('mec_query_params_schema', data.mec_query_params_schema)
            : {},
        mec_request_schema: trim(data.mec_request_schema)
            ? parseJsonOrThrow('mec_request_schema', data.mec_request_schema)
            : {},
        mec_field_mapping: trim(data.mec_field_mapping)
            ? parseJsonOrThrow('mec_field_mapping', data.mec_field_mapping)
            : {},
        mec_default_values: trim(data.mec_default_values)
            ? parseJsonOrThrow('mec_default_values', data.mec_default_values)
            : {},
        mec_target_validation: trim(data.mec_target_validation)
            ? parseJsonOrThrow('mec_target_validation', data.mec_target_validation)
            : {},
        mec_response_schema: parseJsonOrThrow('mec_response_schema', data.mec_response_schema),
        mec_supports_pagination: parseJsonOrThrow('mec_supports_pagination', data.mec_supports_pagination),
        created_by: trim(data.created_by || 'mcp_catalog_ui'),
    };
    if (data.mec_mc_id) body.mec_mc_id = String(data.mec_mc_id).trim();
    if (data.mec_global_tool_id != null && data.mec_global_tool_id !== '') {
        body.mec_global_tool_id = Number(data.mec_global_tool_id);
    }
    return body;
};

export const mcpEndpointInitialFormData = {
    mec_endpoint_key: '',
    mec_connector_key: '',
    mec_operation_key: '',
    version: 1,
    active: true,
    mec_application: '',
    mec_method: 'GET',
    mec_path: '',
    mec_auth_type: 'bearer',
    mec_auth_config:
        '{\n  "mode": "bearer_header",\n  "token_field": "token",\n  "header_name": "Authorization",\n  "prefix": "Bearer "\n}',
    mec_is_secondary_token: false,
    mec_safety_level: 'read',
    mec_default_base_url: '',
    mec_default_headers: '{}',
    mec_default_query_params: '{}',
    mec_path_params_schema: '',
    mec_query_params_schema: '',
    mec_request_schema: '',
    mec_field_mapping: '',
    mec_default_values: '',
    mec_target_validation: '',
    mec_response_schema: '{\n  "list_paths": ["records", "results", "data"]\n}',
    mec_supports_pagination: '{}',
    created_by: 'mcp_catalog_ui',
};

export const mcpEndpointFormFields = [
    {
        name: 'mec_endpoint_key',
        label: 'Endpoint key',
        required: true,
        disableOnEdit: true,
        helperText: 'Stable id, e.g. acc.companies_list',
    },
    {
        name: 'mec_operation_key',
        label: 'Operation key',
        required: true,
        helperText: 'Tenant tool / MCP operation name, e.g. acc_companies_list',
    },
    { name: 'mec_connector_key', label: 'Connector key', required: true },
    { name: 'mec_application', label: 'Application', required: true },
    {
        name: 'version',
        label: 'Version',
        inputType: 'number',
        width: 4,
    },
    { name: 'active', label: 'Active', type: 'checkbox', width: 4 },
    {
        name: 'mec_method',
        label: 'Method',
        type: 'select',
        options: ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'],
    },
    {
        name: 'mec_path',
        label: 'Path',
        required: true,
        width: 12,
        helperText: 'Use {account_id} for path params',
    },
    {
        name: 'mec_auth_type',
        label: 'Auth type',
        type: 'select',
        options: ['bearer', 'api_key', 'none'],
    },
    {
        name: 'mec_is_secondary_token',
        label: 'Use secondary token',
        type: 'checkbox',
        helperText:
            'When checked, MCP uses the connector secondary/app token (ctd_secondary_token) instead of the primary user token. Applies to any connector that stores a secondary token.',
    },
    {
        name: 'mec_safety_level',
        label: 'Safety level',
        type: 'select',
        options: ['read', 'write', 'destructive'],
    },
    { name: 'mec_default_base_url', label: 'Default base URL', width: 12 },
    {
        name: 'mec_auth_config',
        label: 'Auth config (JSON)',
        multiline: true,
        rows: 4,
        width: 12,
    },
    {
        name: 'mec_default_headers',
        label: 'Default headers (JSON)',
        multiline: true,
        rows: 3,
    },
    {
        name: 'mec_default_query_params',
        label: 'Default query params (JSON)',
        multiline: true,
        rows: 3,
    },
    {
        name: 'mec_path_params_schema',
        label: 'Path params schema (JSON)',
        multiline: true,
        rows: 3,
    },
    {
        name: 'mec_query_params_schema',
        label: 'Query params schema (JSON)',
        multiline: true,
        rows: 3,
    },
    {
        name: 'mec_request_schema',
        label: 'Request schema (JSON)',
        multiline: true,
        rows: 4,
        width: 12,
        helperText: 'POST validation: required, forbidden, properties',
    },
    {
        name: 'mec_field_mapping',
        label: 'Field mapping (JSON)',
        multiline: true,
        rows: 4,
    },
    {
        name: 'mec_default_values',
        label: 'Default values (JSON)',
        multiline: true,
        rows: 4,
    },
    {
        name: 'mec_target_validation',
        label: 'Target validation (JSON)',
        multiline: true,
        rows: 3,
        width: 12,
    },
    {
        name: 'mec_response_schema',
        label: 'Response schema (JSON)',
        multiline: true,
        rows: 3,
        width: 12,
    },
    {
        name: 'mec_supports_pagination',
        label: 'Supports pagination (JSON)',
        multiline: true,
        rows: 2,
        width: 12,
    },
    { name: 'created_by', label: 'Created by', width: 12 },
];

/** Global Tools drawer: connector key from tool.mc_id → master connector (read-only). */
export const mcpEndpointFormFieldsForGlobalTool = mcpEndpointFormFields.map((field) => {
    if (field.name === 'mec_connector_key') {
        return {
            ...field,
            disabled: true,
            helperText: 'From this global tool’s linked connector (mc_id).',
        };
    }
    return field;
});

export const mcpEndpointColumns = [
    { field: 'mec_operation_key', headerName: 'Operation key', width: 170 },
    { field: 'mec_endpoint_key', headerName: 'Endpoint key', width: 170 },
    { field: 'mec_connector_key', headerName: 'Connector', width: 110 },
    { field: 'mec_method', headerName: 'Method', width: 80 },
    { field: 'mec_path', headerName: 'Path', width: 220 },
    {
        field: 'active',
        headerName: 'Active',
        width: 80,
        valueGetter: (_value, row) => (row?.active ? 'Yes' : 'No'),
    },
];

export const prepareMcpEndpointEditData = (row) => ({
        ...mcpEndpointInitialFormData,
        mec_mc_id: row.mec_mc_id || '',
        mec_global_tool_id: row.mec_global_tool_id != null ? row.mec_global_tool_id : '',
        mec_endpoint_key: row.mec_endpoint_key || '',
        mec_connector_key: row.mec_connector_key || '',
        mec_operation_key: row.mec_operation_key || '',
        version: row.version != null ? row.version : 1,
        active: row.active !== false,
        mec_application: row.mec_application || '',
        mec_method: row.mec_method || 'GET',
        mec_path: row.mec_path || '',
        mec_auth_type: row.mec_auth_type || 'bearer',
        mec_auth_config: stringifyJsonField(row.mec_auth_config) || '{}',
        mec_is_secondary_token:
            row.mec_is_secondary_token === true || row.mec_is_secondary_token === 'true',
        mec_safety_level: row.mec_safety_level || 'read',
        mec_default_base_url: row.mec_default_base_url || '',
        mec_default_headers: stringifyJsonField(row.mec_default_headers) || '{}',
        mec_default_query_params: stringifyJsonField(row.mec_default_query_params) || '{}',
        mec_path_params_schema: stringifyJsonField(row.mec_path_params_schema),
        mec_query_params_schema: stringifyJsonField(row.mec_query_params_schema),
        mec_request_schema: stringifyJsonField(row.mec_request_schema),
        mec_field_mapping: stringifyJsonField(row.mec_field_mapping) || '',
        mec_default_values: stringifyJsonField(row.mec_default_values) || '',
        mec_target_validation: stringifyJsonField(row.mec_target_validation) || '',
        mec_response_schema: stringifyJsonField(row.mec_response_schema) || '{}',
        mec_supports_pagination: stringifyJsonField(row.mec_supports_pagination) || '{}',
        created_by: row.created_by || 'mcp_catalog_ui',
});

export const fetchMcpCatalogEndpoints = async ({ globalToolId, mcId, connectorKey, operationKey } = {}) => {
    const res = await fetch(buildMcpCatalogListUrl({ globalToolId, mcId, connectorKey, operationKey }), {
        method: 'GET',
        headers: getMcpCatalogAuthHeaders(),
    });
    if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
    const data = await res.json();
    return Array.isArray(data.rows) ? data.rows : [];
};

export const createMcpCatalogEndpoint = async (data) => {
    const body = mapFormToBody(data);
    const res = await fetch(MCP_CATALOG_API_BASE, {
        method: 'POST',
        headers: getMcpCatalogAuthHeaders(),
        body: JSON.stringify(body),
    });
    const payload = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(payload.error || `HTTP error! status: ${res.status}`);
    toast.success('MCP endpoint created successfully');
    return payload;
};

export const updateMcpCatalogEndpoint = async (id, data) => {
    const body = mapFormToBody(data);
    const res = await fetch(`${MCP_CATALOG_API_BASE}/${encodeURIComponent(id)}`, {
        method: 'PUT',
        headers: getMcpCatalogAuthHeaders(),
        body: JSON.stringify(body),
    });
    const payload = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(payload.error || `HTTP error! status: ${res.status}`);
    toast.success('MCP endpoint updated successfully');
    return payload;
};

export const deleteMcpCatalogEndpoint = async (id) => {
    const res = await fetch(`${MCP_CATALOG_API_BASE}/${encodeURIComponent(id)}`, {
        method: 'DELETE',
        headers: getMcpCatalogAuthHeaders(),
    });
    if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
    toast.success('MCP endpoint deleted successfully');
    return true;
};

export const buildMcpEndpointInitialFormForTool = (tool, connectorKey) => {
    const app = connectorKey || '';
    const toolId = tool?.toolId ?? tool?.tool_id;
    const mcId = tool?.mcId ?? tool?.mc_id;
    return {
        ...mcpEndpointInitialFormData,
        mec_mc_id: mcId ? String(mcId) : '',
        mec_global_tool_id: toolId != null ? toolId : '',
        mec_connector_key: connectorKey || '',
        mec_application: app,
        mec_operation_key: tool?.toolUniqueId
            ? String(tool.toolUniqueId).trim()
            : tool?.toolName
              ? String(tool.toolName).trim().toLowerCase().replace(/\s+/g, '_')
              : '',
        created_by: 'global_tools_ui',
    };
};
